import { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faMoneyBillWave,
  faUser,
  faCalendarAlt,
  faFileAlt,
  faPlusCircle,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";
import { toastError, toastSuccess } from "../../../utils/toast";

const AddSalesCloseout = ({
  isOpen,
  onClose,
  onAdd,
  sellers = [],
  isSeller = false,
  mySellerId = null,
}) => {
  const [formData, setFormData] = useState({
    shopSellerId: "",
    amount: "",
    created_at: new Date().toISOString().split("T")[0],
    description: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const activeSellers = sellers.filter(
    (seller) => seller?.status?.toLowerCase() === "active",
  );

  const getSellerId = (seller) =>
    String(seller?.shop_saller_id || seller?.shopSellerId || seller?.id || "");

  const getSellerName = (seller) =>
    seller?.user?.full_name ||
    seller?.full_name ||
    seller?.username ||
    "Unknown Seller";

  useEffect(() => {
    if (isOpen && isSeller && mySellerId) {
      setFormData((prev) => ({
        ...prev,
        shopSellerId: String(mySellerId),
      }));
    }
  }, [isOpen, isSeller, mySellerId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = () => {
    const newErrors = {};
    let errorMessage = "";

    if (!isSeller && !formData.shopSellerId) {
      newErrors.shopSellerId = "Please select a seller";
      errorMessage = "Please select a seller";
    }

    if (!formData.amount) {
      newErrors.amount = "Amount is required";
      errorMessage = "Please enter the total amount collected";
    } else if (
      isNaN(parseFloat(formData.amount)) ||
      parseFloat(formData.amount) <= 0
    ) {
      newErrors.amount = "Please enter a valid amount greater than 0";
      errorMessage = "Please enter a valid amount greater than 0";
    }

    if (!formData.created_at) {
      newErrors.created_at = "Date is required";
      errorMessage = "Please select a date";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      toastError(
        errorMessage || "Please fix all validation errors before continuing.",
      );

      return;
    }

    if (!isSeller) {
      const selectedSeller = activeSellers.find(
        (seller) => getSellerId(seller) === String(formData.shopSellerId),
      );

      if (!selectedSeller) {
        setErrors({
          shopSellerId: "Selected seller is not available",
        });

        toastError("Selected seller is not available");

        return;
      }
    }

    setIsSubmitting(true);

    const payload = {
      shopSellerId: isSeller ? String(mySellerId) : formData.shopSellerId,
      amount: parseFloat(formData.amount),
      created_at: formData.created_at,
      description: formData.description.trim() || null,
    };

    try {
      const result = onAdd(payload);

      if (result && typeof result.then === "function") {
        result
          .then(() => {
            toastSuccess("Sales closeout added successfully!");
            setIsSubmitting(false);
            onClose();
            resetForm();
          })
          .catch((error) => {
            toastError(
              `Failed to add closeout: ${
                error?.message || "Please try again."
              }`,
            );

            setIsSubmitting(false);
          });
      } else {
        toastSuccess("Sales closeout added successfully!");
        setIsSubmitting(false);
        onClose();
        resetForm();
      }
    } catch (error) {
      toastError(
        `Failed to add closeout: ${error?.message || "Please try again."}`,
      );

      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      shopSellerId: "",
      amount: "",
      created_at: new Date().toISOString().split("T")[0],
      description: "",
    });

    setErrors({});
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        resetForm();
      }}
      title="Add Sales Closeout"
      maxWidth="sm:max-w-md"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              onClose();
              resetForm();
            }}
            disabled={isSubmitting}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Cancel
          </Button>

          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                Add closeout
              </>
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faPlusCircle}
              className="text-blue-800 h-4 w-4"
            />
          </div>

          <div>
            <p className="text-sm text-gray-600 font-semibold">
              Record daily sales closing
            </p>

            <p className="text-xs text-gray-600 font-medium">
              Enter total money collected from sales today
            </p>
          </div>
        </div>

        {isSeller ? (
          <div className="flex flex-col gap-1">
            <Label className="text-gray-700 font-medium flex items-center gap-2">
              <FontAwesomeIcon
                icon={faUser}
                className="text-blue-800 h-3.5 w-3.5"
              />
              Seller
            </Label>

            <div className="h-10 px-3 flex items-center rounded-md border border-gray-300 bg-gray-50 text-sm text-gray-700">
              You
            </div>

            <p className="text-xs text-gray-500 italic">
              You can only add closeouts for yourself.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <Label className="text-gray-700 font-medium flex items-center gap-2">
              <FontAwesomeIcon
                icon={faUser}
                className="text-blue-800 h-3.5 w-3.5"
              />
              Seller<span className="text-red-700">*</span>
            </Label>

            <Select
              value={formData.shopSellerId}
              onValueChange={(value) => {
                setFormData((prev) => ({
                  ...prev,
                  shopSellerId: value,
                }));

                if (errors.shopSellerId) {
                  setErrors((prev) => ({
                    ...prev,
                    shopSellerId: "",
                  }));
                }
              }}
            >
              <SelectTrigger
                className={`h-10 text-sm ${
                  errors.shopSellerId ? "border-red-500" : "border-gray-300"
                }`}
              >
                <SelectValue placeholder="Select seller" />
              </SelectTrigger>

              <SelectContent>
                {activeSellers.length > 0 ? (
                  activeSellers.map((seller) => {
                    const sellerId = getSellerId(seller);
                    const sellerName = getSellerName(seller);

                    return (
                      <SelectItem key={sellerId} value={sellerId}>
                        {sellerName}
                      </SelectItem>
                    );
                  })
                ) : (
                  <SelectItem value="no-active-sellers" disabled>
                    No active sellers available
                  </SelectItem>
                )}
              </SelectContent>
            </Select>

            {errors.shopSellerId && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                {errors.shopSellerId}
              </p>
            )}
          </div>
        )}

        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faMoneyBillWave}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Total Amount (TSH)<span className="text-red-700">*</span>
          </Label>

          <Input
            name="amount"
            type="number"
            min="0"
            step="100"
            value={formData.amount}
            onChange={handleChange}
            placeholder="Enter total amount collected"
            className={`border ${
              errors.amount ? "border-red-500" : "border-gray-300"
            } rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
          />

          {errors.amount && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.amount}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faCalendarAlt}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Date<span className="text-red-700">*</span>
          </Label>

          <Input
            name="created_at"
            type="date"
            value={formData.created_at}
            onChange={handleChange}
            className={`border ${
              errors.created_at ? "border-red-500" : "border-gray-300"
            } rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
          />

          {errors.created_at && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.created_at}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium mb-2 flex items-center gap-2">
            <FontAwesomeIcon
              icon={faFileAlt}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Description
          </Label>

          <Textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Optional notes about the sales (e.g., market day, bulk sales)"
            className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30 min-h-[100px] resize-y"
          />
        </div>
      </div>
    </Modal>
  );
};

export default AddSalesCloseout;
